// Cancellations — small shared helpers for the till screens.
import { toLatinDigits } from "@/lib/wmDigits";

export { WM_TIMEOUT_MS as CO_TIMEOUT_MS, wmError as coError, wmMessage as coMessage } from "@/lib/wmError";

/** `t()` has no interpolation; the module's few templates use {name} tokens. */
export function fmt(template, vars) {
    return Object.entries(vars || {}).reduce((s, [k, v]) => s.split(`{${k}}`).join(String(v ?? "")), template);
}

export function labelOf(list, key, lang) {
    const row = (list || []).find((x) => x.key === key);
    if (!row) return key || "";
    return lang === "ar" ? row.name_ar || row.name_en : row.name_en || row.name_ar;
}

export function fmtSar(halalasOrSar, lang, { halalas = true } = {}) {
    if (halalasOrSar === null || halalasOrSar === undefined || halalasOrSar === "") return "";
    const n = halalas ? Number(halalasOrSar) / 100 : Number(halalasOrSar);
    if (Number.isNaN(n)) return "";
    return new Intl.NumberFormat(lang === "ar" ? "ar-SA" : "en-US", { maximumFractionDigits: 2 }).format(n);
}

/** Keep digits and one decimal point; Arabic-Indic folded. */
export function sanitizeValue(raw) {
    let s = toLatinDigits(raw).replace(/[^\d.]/g, "");
    const i = s.indexOf(".");
    if (i !== -1) s = s.slice(0, i + 1) + s.slice(i + 1).replace(/\./g, "");
    return s;
}

export function valueToHalalas(raw) {
    const s = sanitizeValue(raw);
    if (!s || s === ".") return null;
    const n = Number(s);
    return Number.isNaN(n) ? null : Math.round(n * 100);
}

export function fmtTime(iso, lang) {
    if (!iso) return "";
    try {
        return new Intl.DateTimeFormat(lang === "ar" ? "ar-SA" : "en-GB", {
            hour: "2-digit", minute: "2-digit", timeZone: "Asia/Riyadh",
        }).format(new Date(iso));
    } catch {
        return "";
    }
}

/** Compose `occurred_at` from the form's date + time (Riyadh wall clock). */
export function composeOccurredAt(date, time) {
    if (!date || !time) return null;
    return `${date}T${time}:00+03:00`;
}

/** `YYYY-MM-DD` shifted by whole days (UTC arithmetic on a date-only string). */
export function shiftDate(isoDate, days) {
    // "" for a missing or malformed date: toISOString() throws on an invalid
    // Date, and a throw during render blanks the whole page.
    if (!isoDate) return "";
    const d = new Date(`${isoDate}T00:00:00Z`);
    if (Number.isNaN(d.getTime())) return "";
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
}

// The server's photo_status values plus the till's own: `lost` = the server still waits and the
// bytes are nowhere on this device; `checking` / `unchecked` = asking the server about a key.
export const PHOTO_STATUS_KEY = {
    stored: "co_photo_stored", pending: "co_photo_pending", failed: "co_photo_failed", missing: "co_photo_failed",
    uploading: "co_photo_uploading", reserve_failed: "co_photo_reserve_failed", lost: "co_photo_lost",
    checking: "co_photo_checking", unchecked: "co_photo_unchecked",
};

export function fmtDateTime(iso, lang) {
    if (!iso) return "";
    try {
        const s = new Intl.DateTimeFormat(lang === "ar" ? "ar-SA" : "en-GB", {
            year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
            timeZone: "Asia/Riyadh", calendar: "gregory", numberingSystem: "latn",
        }).format(new Date(iso));
        // one LTR-isolated run (bidi marks dropped) so dd/mm/yyyy hh:mm never splits inside an RTL line
        return `\u2066${s.replace(/[\u200e\u200f\u061c]/g, "")}\u2069`;
    } catch {
        return "";
    }
}

/** Review-flag rule → its label (unknown rules show as they are). */
export function flagLabel(t, rule) {
    const k = `co_flag_${rule}`;
    const v = t(k);
    return v === k ? rule : v;
}
